import React from 'react';

import Tag from '@editor/components/Tag/Tag';
import { announcementTheme } from '@editor/components/Tag/theme';
import { FlexRow } from '@editor/components/utilities/layouts';
import { useEditorAndDashboardUpdateFeature } from '@editor/global/growthbookUtils';
import { Badge } from '@repo/ui/components/Badge';
import { IconEventWrapper } from '@repo/ui/components/Icon';
import CloseIcon from '@repo/ui/components/Icon/CloseIcon';
import ArrowLeftIcon from '@repo/ui/components/Icon/v2/ArrowLeftIcon';
import CloseIconV2 from '@repo/ui/components/Icon/v2/CloseIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { themeStyle, useNewThemeActive } from '@repo/ui/theming';
import IconChevronLeft from '@repo/ui-icons/IconChevronLeft';
import IconClose from '@repo/ui-icons/IconClose';
import { Button } from '@repo/ui-kit/components/core/Button';
import { H4 } from '@repo/ui-kit/components/core/Typography';

import {
  Wrapper,
  InnerWrapper,
  Label,
  IconWrapper,
  ClickableArea,
  BackButtonWrapper,
  CloseIconWrapper,
  BackButtonLabel,
  TagWrapper,
  NewThemeLabel,
} from './styles';
import { defaultTheme } from './theme';
import { PanelHeaderProps } from './types';

const betaTagTheme = {
  ...announcementTheme,
  backgroundColor: themeStyle.varBrandSuperLight,
  height: '20px',
};

/**
 * Utility component to create a panel header, with a label and a close button.
 * This component is used as a header for Popover and LeftMenu panels.
 * Optionally, this component can be extended by a Select component, that is placed
 * in front of the label.
 */
const PanelHeader: React.FC<PanelHeaderProps> = ({
  label,
  iconHeight = '10px',
  dataTestId = 'panel-header-close',
  hideCloseIcon,
  backButtonLabel,
  onBackButtonClick,
  closeIconType,
  ...props
}) => {
  const isNewDesignEnabled = useEditorAndDashboardUpdateFeature();

  const theme = { ...defaultTheme, ...props.theme };
  const iconTheme = {
    color: theme.colorIcon,
    activeColor: theme.colorIconActive,
    opacity: theme.opacityIcon,
  };

  const onClose = (event: React.MouseEvent | React.KeyboardEvent): void => {
    props.onClose && props.onClose(event);
  };

  const isNewThemeActive = useNewThemeActive();

  return (
    <>
      {onBackButtonClick &&
        (isNewDesignEnabled ? (
          <>
            <FlexRow alignItems="center" justifyContent="space-between">
              <FlexRow alignItems="center" minWidth="0">
                <Button
                  ariaLabel="Back Button"
                  name="panelHeaderBackButton"
                  rightIcon={IconChevronLeft}
                  size="s"
                  title="Back"
                  variant="ghost"
                  width="24px"
                  onClick={onBackButtonClick}
                />
                <H4 bold textOverflowEllipsis>
                  {label ?? backButtonLabel ?? 'Go Back'}
                </H4>
              </FlexRow>
              <Button
                ariaLabel="Close Panel"
                data-testid={dataTestId}
                name="closePanelButton"
                rightIcon={IconClose}
                size="s"
                title="Close"
                variant="ghost"
                width="24px"
                onClick={onClose}
              />
            </FlexRow>
          </>
        ) : (
          <>
            <Spacer height="8px" />
            <FlexRow justifyContent="space-between">
              <IconEventWrapper
                theme={{
                  color: themeStyle.varInkMedium,
                  activeColor: themeStyle.varInkMain,
                }}
              >
                <BackButtonWrapper onClick={onBackButtonClick}>
                  <ArrowLeftIcon height="14px" theme={iconTheme} />
                  <BackButtonLabel>
                    {backButtonLabel || 'Go Back'}
                  </BackButtonLabel>
                </BackButtonWrapper>
              </IconEventWrapper>
              <CloseIconWrapper data-testid={dataTestId} onClick={onClose}>
                <IconEventWrapper theme={iconTheme}>
                  <ClickableArea />
                  <CloseIconV2 height="14px" theme={iconTheme} />
                </IconEventWrapper>
              </CloseIconWrapper>
            </FlexRow>
            <Spacer height="14px" />
          </>
        ))}
      <Wrapper>
        {props.labelPrefix && <props.labelPrefix />}
        <InnerWrapper hasLabelPrefix={!!props.labelPrefix}>
          {props.customElement}
          {onBackButtonClick && isNewDesignEnabled
            ? null
            : label &&
              (isNewThemeActive ? (
                <NewThemeLabel theme={theme}>
                  <b>{label}</b>
                </NewThemeLabel>
              ) : (
                <Label theme={theme}>{label}</Label>
              ))}
          {props.isBeta && (
            <TagWrapper>
              {isNewThemeActive ? (
                <Badge text="Beta" variant="blue" />
              ) : (
                <Tag theme={betaTagTheme}>Beta</Tag>
              )}
            </TagWrapper>
          )}
          {props.onClose && !hideCloseIcon && !onBackButtonClick && (
            <IconWrapper data-testid={dataTestId} onClick={onClose}>
              {closeIconType === 'button' ? (
                <Button
                  ariaLabel="Close Panel"
                  data-testid="panel-header--close-icon"
                  name="panelHeaderCloseButton"
                  rightIcon={IconClose}
                  size="s"
                  title="Close Panel"
                  variant="ghost"
                  width="32px"
                  onClick={onClose}
                />
              ) : (
                <IconEventWrapper theme={iconTheme}>
                  <ClickableArea />
                  <CloseIcon height={iconHeight} theme={iconTheme} />
                </IconEventWrapper>
              )}
            </IconWrapper>
          )}
        </InnerWrapper>
      </Wrapper>
    </>
  );
};

export default PanelHeader;
